import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/global/components/ui/card'
import { Tooltip } from '@/global/components/ui/tooltip'
import dayjs from 'dayjs'
import { Link } from 'global/components/Link'
import { periodicidade } from 'locales/plan'
import { TriangleAlertIcon } from 'lucide-react'
import { usePlanosContext } from 'pages/Planos'
import { clientes } from 'service/api'

export default function GridDados() {
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 w-full">
            <Card className="text-muted-foreground">
                <DadosContrato />
            </Card>
            <Card className="text-muted-foreground">
                <DadosPagamento />
            </Card>
        </div>
    )
}

function DadosContrato() {
    const { assinatura, planos } = usePlanosContext()
    const meuPlano = planos?.find(
        it =>
            it.limiteFuncionarios ===
                Number(assinatura?.additionalInfo?.replace(/\D/g, '')) &&
            it.periodicity === assinatura?.periodicity,
    )

    const { data } = clientes.useFetchById<{
        ativos: number
        limite: number
    }>('limites')

    const { ativos, limite } = data || { ativos: 0, limite: 0 },
        isBloqueado = ativos > limite,
        isMax = ativos === limite

    return (
        <>
            <CardHeader>
                <CardTitle>Dados do contrato</CardTitle>
            </CardHeader>
            <CardContent className="h-auto space-y-3 overflow-auto text-sm sm:h-[13.5rem] sm:text-base">
                <p className="inline-flex flex-wrap items-center gap-1">
                    Funcionários cadastrados:{' '}
                    <span
                        className={(() => {
                            if (isBloqueado) return 'text-destructive'
                            if (isMax) return 'text-primary'
                            return 'text-muted-foreground'
                        })()}
                    >
                        <span className="inline-flex items-center gap-1">
                            <span>
                                {ativos}/{limite}
                            </span>
                            {isBloqueado && (
                                <Tooltip
                                    title={
                                        <span>
                                            Você ultrapassou o limite de
                                            funcionários ativos.
                                            <br />
                                            <br />
                                            Altere seu plano ou exclua
                                            funcionários para continuar usando o
                                            sistema.
                                            <br />
                                            Depois faça login novamente para que
                                            o sistema seja liberado.
                                        </span>
                                    }
                                >
                                    <TriangleAlertIcon className="size-4 text-red-500" />
                                </Tooltip>
                            )}
                            {isMax && (
                                <Tooltip
                                    title={
                                        <span>
                                            Você atingiu o limite de
                                            funcionários ativos.
                                            <br />
                                            Para cadastrar mais funcionários,
                                            altere seu plano.
                                        </span>
                                    }
                                >
                                    <TriangleAlertIcon className="size-4 text-yellow-500" />
                                </Tooltip>
                            )}
                        </span>
                    </span>
                </p>
                {assinatura ? (
                    <>
                        <p>
                            Plano: {(() => {
                                if (!meuPlano) return 'Plano não encontrado'
                                if (meuPlano.limiteFuncionarios == 0)
                                    return 'Especial'
                                return `até ${meuPlano.limiteFuncionarios} funcionários`
                            })()}
                        </p>
                        <p>
                            Periodicidade:{' '}
                            {periodicidade[assinatura?.periodicity]}
                        </p>
                        <p>
                            Data da 1ª cobrança:{' '}
                            {dayjs(assinatura?.firstPayDayDate).format(
                                'DD/MM/YYYY',
                            )}
                        </p>
                        <p>
                            Qtde de Transações:{' '}
                            {assinatura?.quantity === 0 ? (
                                <Tooltip
                                    title={
                                        <span>
                                            Faltando 30 dias para o vencimento,
                                            a
                                            <br />
                                            próxima transação é adicionada
                                            <br />
                                            automaticamente no sistema. O
                                            sistema só
                                            <br />
                                            para de lançar automaticamente
                                            quando o
                                            <br />
                                            contrato é cancelado manualmente.
                                            <br />
                                        </span>
                                    }
                                >
                                    <Link className="cursor-default">
                                        Indeterminado
                                    </Link>
                                </Tooltip>
                            ) : (
                                assinatura?.quantity
                            )}
                        </p>
                        <p>
                            Email financeiro:{' '}
                            {assinatura?.Customer?.emails?.at(-1)}
                        </p>
                    </>
                ) : (
                    <p>
                        Não está vendo os dados sobre sua assinatura? Ela pode
                        ter sido cancelada ou estar com algum problema. Entre em
                        contato com o suporte para mais informações.
                    </p>
                )}
            </CardContent>
        </>
    )
}

function DadosPagamento() {
    const { assinatura } = usePlanosContext()

    if (!assinatura) return <CardContent className="h-auto sm:h-[13.5rem]" />

    return (
        <>
            <CardHeader>
                <CardTitle>Dados de pagamento</CardTitle>
            </CardHeader>
            <CardContent className="h-auto space-y-3 overflow-auto text-sm sm:h-[13.5rem] sm:text-base">
                {assinatura?.PaymentMethodBoleto && (
                    <div className="space-y-3">
                        <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
                            Dados do boleto
                        </h3>
                        <p>
                            Multa por atraso:{' '}
                            {(
                                assinatura?.PaymentMethodBoleto?.fine! / 100
                            ).toFixed(2)}
                            %
                        </p>
                        <p>
                            Juros ao mês:{' '}
                            {(
                                assinatura?.PaymentMethodBoleto?.interest! / 100
                            ).toFixed(2)}
                            %
                        </p>
                        <p>
                            Prazo máximo emissão de 2º via:{' '}
                            {assinatura?.PaymentMethodBoleto?.deadlineDays} dias
                        </p>
                        <p>
                            Valor das próximas transações a serem geradas:{' '}
                            {(assinatura?.value! / 100).toLocaleString(
                                'pt-br',
                                {
                                    style: 'currency',
                                    currency: 'BRL',
                                },
                            )}
                        </p>
                    </div>
                )}
                {assinatura?.PaymentMethodCreditCard && (
                    <div className="space-y-3">
                        <h3 className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
                            Dados do cartão
                        </h3>
                        <p>
                            Valor das próximas transações a serem geradas:{' '}
                            {(assinatura?.value! / 100).toLocaleString(
                                'pt-br',
                                {
                                    style: 'currency',
                                    currency: 'BRL',
                                },
                            )}
                        </p>
                        <p>
                            Cartão das próximas transações a serem geradas:{' '}
                            <strong>
                                {
                                    assinatura.PaymentMethodCreditCard.Card
                                        ?.Brand?.name
                                }{' '}
                                **** ****{' '}
                                {assinatura?.PaymentMethodCreditCard?.Card?.number?.slice(
                                    -4,
                                )}
                            </strong>
                        </p>
                        <p>Multa por atraso: R$ 16,00</p>
                        <p>Juros ao mês: R$ 8,00 (R$ 0,27 ao dia)</p>
                    </div>
                )}
            </CardContent>
        </>
    )
}
